import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { CompanyFilter } from "@/components/CompanyFilter";
import { Select, SearchInput } from "@/components/inputs";
import { POLL } from "@/lib/format";

import { assetData } from "@/api/client";
import { useSticky } from "@/lib/hooks/useSticky";

//: Fallback asset types — the catalogue's asset kinds, for a caller whose own
//: data has none yet. The server's `/asset-data/asset-types` is preferred (it
//: carries counts) and this is only used when it comes back empty.
const ASSET_TYPES: [string, string][] = [
  ["vehicle", "Vehicle"], ["battery", "Battery"],
];

// static mirror of the catalog kinds (the picker must work for customer roles
// that cannot fetch the internal /asset-catalog/templates endpoint)
const PART_KINDS: [string, string][] = [
  ["tcu", "Telematics unit"], ["bms_c", "Connected BMS"], ["vcu_c", "Connected VCU"],
  ["cluster_c", "Connected Cluster"], ["cell", "Cell"], ["bms", "BMS"], ["motor", "Motor"],
  ["mcu", "Motor controller"], ["dcdc", "DC-DC converter"], ["vcu", "VCU"], ["cluster", "Cluster"],
];

/** One picker, three identities, one flow: Company → Search by →
 *  (Asset type | Subcomponent kind) → search → result. Everything resolves to
 *  the Connection ID whose stream to open; matches without a stream (dumb
 *  part, asset with no connected part) are disabled WITH the reason. */
export function DevicePicker({ imei, onPick, onPickAsset, onFilters }: {
  imei: string;
  onPick: (imei: string) => void;
  /** Reports the ASSET the match resolved to, for a caller that acts on the
   *  asset rather than on its data stream — Asset Control places a hold, and a
   *  hold is a lien on the asset (D-35: it outlives the box). Fired with null
   *  when the search was by part or IMEI, where there is no asset to name. */
  onPickAsset?: (assetId: string | null) => void;
  /** Reports the COMPANY and ASSET-TYPE choices outward, for a caller that
   *  shows something of its own under them — the fleet map filters its dots by
   *  exactly these. It exists so the Overview map can render THIS picker
   *  instead of a second company dropdown beside it: two filters for one
   *  question is how they end up disagreeing, and the console has already paid
   *  for that (CompanyFilter's own docstring). */
  onFilters?: (f: { company: string; asset_type: string }) => void;
}) {
  const [company, setCompany] = useSticky("live", "company", "");
  const [by, setBy] = useSticky("live", "by", "asset");
  const [atype, setAtype] = useSticky("live", "atype", "");
  const [kind, setKind] = useSticky("live", "kind", "");
  const [search, setSearch] = useSticky("live", "search", "");

  const companies = useQuery({ queryKey: ["live", "companies"], queryFn: assetData.companies });
  // Report the filters up on every change AND on mount, so a caller never
  // renders its first view against defaults the picker does not hold (the
  // sticky values survive navigation, so "" is not a safe assumption).
  useEffect(() => { onFilters?.({ company, asset_type: atype }); },
            [company, atype, onFilters]);

  // The asset-type options come from the DATA where the server can supply them
  // — the types this caller actually has, which is what the map's bubbles are
  // counting. The static list below stays as the fallback: a company with no
  // typed assets yet would otherwise get a dropdown with nothing in it, and
  // the catalogue kinds are real whether or not this tenant owns one.
  const types = useQuery({
    queryKey: ["live", "asset-types", company],
    queryFn: () => assetData.assetTypes(company || undefined),
  });
  const typeOpts: [string, string][] =
    (types.data?.asset_types ?? []).length
      ? (types.data!.asset_types).map((t) => [t.asset_type,
          `${t.asset_type} (${t.count})`] as [string, string])
      : ASSET_TYPES;
  const devices = useQuery({
    queryKey: ["live", "devices", company, search],
    queryFn: () => assetData.devices({ company: company || undefined, search: search || undefined }),
    refetchInterval: POLL.LIST,
    enabled: by === "connection",
  });
  const found = useQuery({
    queryKey: ["live", "find", by, search, company, atype, kind],
    queryFn: () => assetData.find(by as "asset" | "part", search, {
      company: company || undefined,
      asset_type: by === "asset" ? atype || undefined : undefined,
      kind: by === "part" ? kind || undefined : undefined,
    }),
    enabled: by !== "connection" && search.trim().length >= 2,
  });
  const comps = companies.data?.companies ?? [];
  const devs = devices.data?.devices ?? [];
  const matches = found.data?.matches ?? [];

  return (
    <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
      <CompanyFilter companies={comps} scoped={companies.data?.scoped ?? false}
                     value={company}
                     onChange={(v: string) => { setCompany(v); onPick(""); }} />
      <div>
        <label className="simlab">Search by</label>
        <Select style={{ width: 190, minWidth: 0 }} value={by}
          onChange={(e) => { setBy(e.target.value); setSearch(""); onPick(""); }}>
          <option value="asset">Asset (VIN / BIN / plate)</option>
          <option value="part">Subcomponent (serial / board)</option>
          <option value="connection">IMEI (connection)</option>
        </Select>
      </div>

      {by === "asset" && (
        <div>
          <label className="simlab">Asset type</label>
          <Select style={{ width: 150, minWidth: 0 }} value={atype}
            onChange={(e) => { setAtype(e.target.value); onPick(""); }}>
            <option value="">All</option>
            {typeOpts.map(([id, label]) => (
              <option key={id} value={id}>{label}</option>
            ))}
          </Select>
        </div>
      )}
      {by === "part" && (
        <div>
          <label className="simlab">Subcomponent</label>
          <Select style={{ width: 170, minWidth: 0 }} value={kind}
            onChange={(e) => { setKind(e.target.value); onPick(""); }}>
            <option value="">All kinds</option>
            {PART_KINDS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
          </Select>
        </div>
      )}

      {by === "connection" ? (
        <>
          <div>
            <label className="simlab">Filter IMEI</label>
            <SearchInput mono style={{ width: 160, minWidth: 0 }}
              placeholder="substring…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="simlab">Device (IMEI)</label>
            <Select className="mono" style={{ width: 240, minWidth: 0 }}
              value={imei} onChange={(e) => {
                onPick(e.target.value);
                onPickAsset?.(null);   // an IMEI search names no asset
              }}>
              <option value="">select…</option>
              {devs.map((d) => (
                <option key={d.imei} value={d.imei}>
                  {d.imei}
                  {d.company ? ` · ${d.company}` : d.company_id === "__inventory__" ? " · inventory" : ""}
                  {!d.registered ? " · NOT ENROLLED" : d.online ? " ●" : d.has_data ? "" : " · no data yet"}
                </option>
              ))}
            </Select>
          </div>
          <span style={{ fontSize: 11.5, color: "var(--t-mute)", paddingBottom: 6 }}>
            {devs.length} device{devs.length === 1 ? "" : "s"}
          </span>
        </>
      ) : (
        <>
          <div>
            <label className="simlab">{by === "asset" ? "VIN / BIN / number plate" : "Serial / board id"}</label>
            <SearchInput mono style={{ width: 220, minWidth: 0 }} placeholder="type at least 2 characters…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="simlab">{by === "asset" ? "Asset" : "Match"}</label>
            <Select style={{ width: 300, minWidth: 0 }} value={imei}
              onChange={(e) => {
                onPick(e.target.value);
                // the row the operator actually chose, by index — matching on
                // `imei` would pick the wrong row when two assets share a
                // stream-less state (both have imei "")
                const picked = matches[Number(e.target.selectedIndex) - 1];
                onPickAsset?.(picked?.asset_id ?? null);
              }}>
              <option value="">{matches.length ? "select…" : search.trim().length >= 2 ? "no matches" : "search first…"}</option>
              {matches.map((m, i) => (
                <option key={i} value={m.imei ?? ""} disabled={!m.imei}>
                  {m.label} · {m.sub}{m.reason ? ` - ${m.reason}` : ""}
                </option>
              ))}
            </Select>
          </div>
          {imei && <span className="mono" style={{ fontSize: 11.5, color: "var(--t-mute)", paddingBottom: 6 }}>stream: {imei}</span>}
        </>
      )}
    </div>
  );
}
